import {defineStore} from 'pinia';
import {getToken} from '@/services/authStorage';
import {
    createTicket,
    downloadTicketAttachment,
    getTicket,
    getTicketsUnreadCount,
    getTicketsUnreadMap,
    listTickets,
    markTicketAsRead,
    postMessage,
    updateTicketStatus,
} from '@/api/tickets';

export const useTicketsStore = defineStore('tickets', {
  state: () => ({
    list: [],
    current: null, // Detail mit Thread
    loading: false,
    error: null,
    unreadCount: 0,
    unreadMap: {},
    // Throttle für Unread-Count
    unreadLastAt: 0,
    unreadLoading: false,
  }),
  actions: {
    async fetchList(params = {}) {
      this.loading = true;
      this.error = null;
      try {
        const data = await listTickets(params);
        this.list = Array.isArray(data) ? data : [];
        // Nach dem Laden der Liste auch die Unread-Map nachziehen (best effort)
        try {
          await Promise.allSettled([this.fetchUnreadCount(), this.fetchUnreadMap()]);
        } catch { /* ignore */ }
      } catch (e) {
        this.error = e?.response?.data?.message || e?.message || 'Unbekannter Fehler';
        throw e;
      } finally {
        this.loading = false;
      }
    },
    async fetchUnreadCount({ force = false } = {}) {
      // Bei fehlendem Token keine Anfrage starten
      const token = getToken() || '';
      if (!token || !token.trim()) {
        this.unreadCount = 0;
        this.unreadLastAt = Date.now();
        return;
      }

      const now = Date.now();
      // Throttle: höchstens alle 30s und nicht parallel
      if (!force) {
        if (this.unreadLoading) return;
        if (now - (this.unreadLastAt || 0) < 30_000) return;
      }
      this.unreadLoading = true;
      try {
        const data = await getTicketsUnreadCount();
        const n = Number(data?.count ?? 0);
        this.unreadCount = Number.isFinite(n) ? n : 0;
      } catch {
        // Bei Fehler Zähler nicht hart auf 0 setzen, behalte letzten Wert
      } finally {
        this.unreadLastAt = Date.now();
        this.unreadLoading = false;
      }
    },
    async fetchUnreadMap() {
      // Bei fehlendem Token keine Anfrage starten
      const token = getToken() || '';
      if (!token || !token.trim()) {
        this.unreadMap = {};
        return;
      }
      try {
        const data = await getTicketsUnreadMap();
        const map = data?.map && typeof data.map === 'object' ? data.map : {};
      this.unreadMap = map || {};
      } catch {
        this.unreadMap = {};
      }
    },
    async fetchTicket(id) {
      this.loading = true;
      this.error = null;
      try {
        const data = await getTicket(id);
        this.current = data || null;
      } catch (e) {
        this.error = e?.response?.data?.message || e?.message || 'Unbekannter Fehler';
        throw e;
      } finally {
        this.loading = false;
      }
    },
    async markRead(id) {
      try {
        await markTicketAsRead(id);
      } catch {
        // ignore
      } finally {
        // Zähler und Map aktualisieren (best effort) – Count forcen, damit er sofort stimmt
        await Promise.allSettled([this.fetchUnreadCount({ force: true }), this.fetchUnreadMap()]);
      }
    },
    async create({ subject, message, attachments = [], isPrivate = false }) {
        // Neue Konversation enthält eigene erste Nachricht -> Zähler bleibt
      return await createTicket({subject, message, attachments, isPrivate});
    },
    async reply(id, { message, attachments = [] }) {
      await postMessage(id, { message, attachments });
      await this.fetchTicket(id);
      // Antwort vom aktuellen Nutzer sollte den Zähler nicht erhöhen, dennoch neu laden
      await this.fetchUnreadCount();
    },
    async setStatus(id, status) {
      await updateTicketStatus(id, status);
      await this.fetchList();
      if (this.current?.id === id) {
        await this.fetchTicket(id);
      }
      await this.fetchUnreadCount();
    },
    async downloadAttachment(ticketId, documentId) {
      return downloadTicketAttachment(ticketId, documentId);
    },
  },
});
